import { getOverriddenMock } from "../ServerApi/TestingRouteOverrides";
import { getStore } from "../VerificationService/VerificationServiceStore";
import { setOptions } from "../../options/options";
import { type MockStep, type Options } from "../types/types";

import { programStore, type MockOverrides } from "./programStore";

// Preview mock directives map onto the existing `mock*` options that getOverriddenMock reads.
const MOCK_OPTION_KEYS: Partial<Record<keyof MockOverrides, keyof Options>> = {
  step: "mockStep",
  previousStep: "mockPreviousStep",
  segment: "mockSegment",
  subSegment: "mockSubSegment",
  errorId: "mockErrorId",
  errorDetailId: "mockErrorDetailId",
  captchaState: "mockCaptchaState",
  rewardCode: "mockRewardCode",
  redirectUrl: "mockRedirectUrl",
  consumerInfoState: "mockConsumerInfoState",
  idCheckLoopServiceType: "mockIdCheckLoopServiceType",
  docSelected: "mockDocSelected",
};

/**
 * Preview a mocked verification response (step, redirect URL, reward code, error, …). Bridges to the
 * still-Redux verificationResponse: reuses getOverriddenMock, then dispatches in place — no teardown.
 */
export async function setPreviewMock(directives: MockOverrides): Promise<void> {
  // Feed the existing options-based mock machinery (getOverriddenMock reads these).
  const optionPatch: Partial<Options> = {};
  (Object.keys(directives) as (keyof MockOverrides)[]).forEach((key) => {
    const optionKey = MOCK_OPTION_KEYS[key];
    if (optionKey) (optionPatch as Record<string, unknown>)[optionKey] = directives[key];
  });
  setOptions(optionPatch);

  // Build the full per-step mock (reusing getOverriddenMock for parity) before touching the store,
  // so mock directives and the resolved response land in one atomic update — no intermediate render.
  const step = directives.step ?? programStore.get().overrides.mock?.step;
  const response = step
    ? await getOverriddenMock(step as MockStep, getStore().getState().verificationResponse)
    : undefined;

  programStore.set((prev) => ({
    ...prev,
    overrides: {
      ...prev.overrides,
      mock: { ...prev.overrides.mock, ...directives },
      ...(response ? { response } : {}),
    },
  }));
}
